import { coreActions } from '@sonar-software/sonar-core';
import { createAsyncSelectors } from '@lib/selectors';
import { EditOutlined } from '@ant-design/icons';
import { SonarText } from '@ui/text';
import React, { useCallback, useEffect, useState } from 'react';
import { useAction, useSonarSelector } from '@hooks';

export const patchUserSelectors = createAsyncSelectors((state) => state.core.users.byId.patch);

export interface UserTableEditableCellProps {
	editable?: boolean;
	userProperty: string;
	value?: string;
	userId: number;
	shardName?: string;
}

export const UserTableEditableCell: React.FC<UserTableEditableCellProps> = (props) => {
	const { editable = true, userProperty, value, userId, shardName } = props;

	const patchUserIsErrored = useSonarSelector(patchUserSelectors.isErrored);
	const patchUserIsFailed = useSonarSelector(patchUserSelectors.isFailed);
	const patchUserIsRequested = useSonarSelector(patchUserSelectors.isRequested);
	const patchUserIsReceived = useSonarSelector(patchUserSelectors.isReceived);
	const patchUser = useAction(coreActions.requestPatchCoreUsersById);

	const [ isEditing, setIsEditing ] = useState(false);
	const [ isSubmitted, setIsSubmitted ] = useState(false);
	const [ previousValue, setPreviousValue ] = useState(value);
	const [ displayedValue, setDisplayedValue ] = useState(value);

	const handleEditStarted = useCallback(() => {
		if (!patchUserIsRequested) {
			setIsEditing(true);
		}
	}, [ patchUserIsRequested ]);

	const handleEditCanceled = useCallback(() => {
		setIsEditing(false);
	}, []);

	const handleChange = useCallback(
		(newValue: string) => {
			if (newValue && !patchUserIsRequested) {
				setIsEditing(false);
				if (newValue !== displayedValue) {
					setIsSubmitted(true);
					setPreviousValue(displayedValue);
					setDisplayedValue(newValue);
					patchUser({
						shardName,
						userId: userId,
						[ userProperty ]: newValue
					});
				}
			}
		},
		[ patchUser, displayedValue, userProperty, userId, shardName, patchUserIsRequested ]
	);

	useEffect(() => {
		if (patchUserIsReceived && isSubmitted) {
			setIsSubmitted(false);
		}
	}, [ patchUserIsReceived, isSubmitted ]);

	useEffect(() => {
		if ((patchUserIsErrored || patchUserIsFailed) && isSubmitted) {
			setDisplayedValue(previousValue);
			setIsSubmitted(false);
		}
	}, [ patchUserIsErrored, patchUserIsFailed, isSubmitted, previousValue ]);

	useEffect(() => {
		setDisplayedValue(value);
		setPreviousValue(value);
	}, [ value ]);

	if (editable) {
		return (
			<div className='editable-table-cell'>
				<SonarText
					editable={ {
						editing: isEditing && !patchUserIsRequested,
						icon: <EditOutlined />,
						onCancel: handleEditCanceled,
						onChange: handleChange,
						onStart: handleEditStarted
					} }>
					{ displayedValue }
				</SonarText>
			</div>
		);
	}

	return <span>{ displayedValue }</span>;
};
